import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@one-thd/sui-atomic-components';
import { ArrowForward } from '@one-thd/sui-icons';
import { useLocalStorage } from '@thd-olt-global/thd-storage-utils';
import { PIP_QUESTIONS } from '../../MagicApronAssistant/subcomponents/constants';
import { TileButton } from '../../common/TileButton';
import { waitForRefValue, getRandomElements } from '../../../utils/common';
import {
  MESSAGE_ORIGIN_PREPOPULATED,
  ASK_A_QUESTION_ANALYTICS_PLACEHOLDER
} from '../../MagicApronAssistant/constants';
import { getPageType } from '../../../utils/window-utils';
import { createActionId, getDefaultSession } from '../../../utils/session-utils';

const randomSuggestQuestions = getRandomElements(PIP_QUESTIONS);
const defaultSession = getDefaultSession();

export const EntryPointSuggestedQuestions = ({ entryLocationForAnalytics }) => {
  const suggestedQuestions = randomSuggestQuestions;
  const maaBroadcastChannel = useRef(null);
  const maaComponentReady = useRef({ isReady: false, resolver: null });
  const [session = defaultSession] = useLocalStorage('maa-session', defaultSession);
  const pageType = getPageType();

  useEffect(() => {
    maaBroadcastChannel.current = new BroadcastChannel('magic-apron-assistant');

    const isReadyListener = ({ output: { isReady } }) => {
      maaComponentReady.current.isReady = isReady;

      if (isReady && maaComponentReady.current.resolver) {
        maaComponentReady.current.resolver();
      }
    };

    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('magic-apron-assistant.isReady', isReadyListener);

    return () => {
      maaBroadcastChannel.current.close();
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.off('magic-apron-assistant.isReady', isReadyListener);
    };
  }, []);

  const handleQuestionClick = async (question, position) => {
    maaBroadcastChannel.current.postMessage({
      type: 'MAA_OPEN_DRAWER',
    });

    if (question) {
      const skipWait = maaComponentReady.current.isReady;
      return waitForRefValue(maaComponentReady, 'isReady', skipWait)
        .then(() => {
          window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('magic-apron-assistant.user-ask-question', {
            question,
            entryLocationForAnalytics,
            pageType: pageType.toLowerCase(),
            messageOrigin: MESSAGE_ORIGIN_PREPOPULATED,
            position,
          });
        })
        // eslint-disable-next-line no-console
        .catch((err) => { console.error(err); });
    }
    return Promise.resolve();
  };

  const handleAskAQuestionClick = () => {
    maaBroadcastChannel.current.postMessage({
      type: 'MAA_OPEN_DRAWER',
    });

    const sessionID = session.sessionId;
    const actionID = createActionId(sessionID);

    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.question-ask', {
      userQuestion: ASK_A_QUESTION_ANALYTICS_PLACEHOLDER,
      entryLocationForAnalytics,
      pageType: pageType.toLowerCase(),
      sessionID,
      actionID
    });
  };

  return (
    <div className="sui-flex sui-flex-row sui-flex-wrap sui-gap-4">
      {suggestedQuestions.map((question, i) => (
        <div className="sui-flex" key={i}>
          <TileButton
            onClick={() => handleQuestionClick(question, i)}
            value={question}
            data-testid="suggested-question"
            lineClamp={2}
            withIcon
          />
        </div>
      ))}
      <div className="sui-flex sui-shrink-0" key="ask-prompt">
        <div className="sui-bg-primary">
          <Button variant="secondary" onClick={() => handleAskAQuestionClick()}>
            <div className="sui-flex sui-items-center sui-gap-2">
              Ask a Question  <ArrowForward size="small" />
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

EntryPointSuggestedQuestions.propTypes = {
  entryLocationForAnalytics: PropTypes.string,
};

EntryPointSuggestedQuestions.defaultProps = {
  entryLocationForAnalytics: '',
};
